import { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import DashboardTemplate from "@/user/DashboardTemplate";
import { DASHBOARDS } from "@/user/dashboards.config";
import { Button } from "@/admin/ui";
import { formsForRole, COMING_SOON } from "@/user/workspace/forms.config";
import { PageHeader, ComingSoonGrid, ActionIcon, StatusPill } from "@/user/workspace/wsui";
import { ws, fmtDate } from "@/user/workspace/ws.api";

/** Role dashboard. CEO keeps the command-center template; department roles
 * get a live, submission-driven workspace dashboard. */
export default function RoleDashboard({ role }) {
    const config = DASHBOARDS[role];
    const { user } = useOutletContext();

    // CEO / roles without capture forms → keep the command center template.
    if (role === "ceo" || formsForRole(role).length === 0) {
        if (!config) {
            return (
                <div className="text-neutral-600">
                    <div className="font-display text-3xl">Workspace</div>
                    <p className="mt-3 text-[14px]">Your role does not have a dedicated workspace yet.</p>
                </div>
            );
        }
        return <DashboardTemplate config={config} />;
    }

    return <DepartmentDashboard role={role} config={config} user={user} />;
}

function DepartmentDashboard({ role, config, user }) {
    const nav = useNavigate();
    const [summary, setSummary] = useState(null);
    const forms = formsForRole(role).slice(0, 6);

    useEffect(() => { ws.summary().then(setSummary).catch(() => {}); }, []);

    const cards = [
        { label: "Total Submissions", value: summary?.total ?? "—" },
        { label: "Submitted", value: summary?.submitted ?? "—" },
        { label: "Pending Drafts", value: summary?.drafts ?? "—", tone: "warning" },
        { label: "This Week", value: summary?.this_week ?? "—" },
        { label: "Files", value: summary?.files ?? "—" },
        { label: "Tasks Pending", value: summary?.tasks_pending ?? "—", tone: "warning" },
    ];

    return (
        <div data-testid="dashboard-page">
            <PageHeader
                eyebrow={config?.eyebrow || role}
                title={config?.title || "Workspace"}
                subtitle={config?.purpose}
                actions={<Button variant="primary" onClick={() => nav("/app/workspace")} data-testid="dash-open-workspace">Open My Workspace →</Button>}
            />

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-9">
                {cards.map((c, i) => (
                    <div key={i} data-testid={`kpi-${i}`} className="glass rounded-2xl p-4">
                        <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-neutral-500">{c.label}</div>
                        <div className={`mt-2 font-display text-2xl font-medium leading-none ${c.tone === "warning" ? "text-amber-600" : "text-black"}`}>{c.value}</div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                    <h2 className="font-display text-lg font-medium text-black mb-4">Quick Actions</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {forms.map((f) => (
                            <button key={f.key} onClick={() => nav("/app/workspace")} data-testid={`dash-form-${f.key}`}
                                className="group text-left glass rounded-2xl p-4 flex items-center gap-3 hover:-translate-y-0.5 transition-all duration-300">
                                <span className="h-9 w-9 rounded-xl bg-violet-50 text-violet-600 inline-flex items-center justify-center group-hover:bg-black group-hover:text-white transition-colors">
                                    <ActionIcon name={f.icon} />
                                </span>
                                <span className="text-[14px] font-medium text-black">{f.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <h2 className="font-display text-lg font-medium text-black mb-4">Recent Updates</h2>
                    <div className="glass rounded-2xl overflow-hidden">
                        {summary?.recent?.length ? (
                            <div className="divide-y divide-black/5">
                                {summary.recent.map((s) => (
                                    <button key={s.id} onClick={() => nav("/app/submissions")} data-testid={`dash-recent-${s.id}`} className="w-full text-left px-4 py-3 hover:bg-black/[0.02]">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="text-[13px] font-medium text-black truncate">{s.title}</div>
                                            <StatusPill status={s.status} />
                                        </div>
                                        <div className="text-[10.5px] text-neutral-400 font-mono-alt mt-0.5">{s.submission_id} · {fmtDate(s.updated_at)}</div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="p-6 text-[13px] text-neutral-500">No submissions yet. Create your first update from My Workspace.</div>
                        )}
                    </div>
                </div>
            </div>

            <ComingSoonGrid items={COMING_SOON} />
        </div>
    );
}
